import { isEmpty } from 'lodash';
import type React from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import { MediaItem } from 'src/types/quote-approval';

const Loader: React.FC = () => (
  <div className="preview-image-inner">
    <div className="common-loading-upload" />
    <div className="common-loading-upload" />
    <div className="common-loading-upload" />
  </div>
);

interface PhotosProps {
  photos: MediaItem[];
  sectionName: string;
  jobRootNodeId?: number;
  thumbnailLimit: number;
  imageType: string;
  isLoading: boolean;
  timeZone: string | undefined;
}

const Photos: React.FC<PhotosProps> = ({
  photos,
  sectionName,
  jobRootNodeId,
  thumbnailLimit,
  imageType,
  isLoading,
  timeZone,
}) => {
  const dispatch = useAppDispatch();

  const renderPhotos = (): React.ReactNode => {
    if (!isEmpty(photos)) {
      const numberOfPhotos = photos?.length;

      return (
        <div className="preview-image-inner">
          {photos?.slice(0, thumbnailLimit)?.map((photo, index) => (
            <div
              key={photo?.job_media_id}
              className={[
                'preview-image',
                photo?.media_type === 'video'
                  ? 'video' : '',
              ].join(' ')}
            >
              <img
                onClick={() => {
                  dispatch(
                    updateVisibility({
                      modalType: 'imageSlidesModal',
                      isVisible: true,
                      extraProps: {
                        modalData: {
                          collection: photos,
                          position: index,
                          showDeleteButton: false,
                          imageType,
                          jobRootNodeId,
                          hasAbilityToDownload: true,
                          timeZone,
                        },
                      },
                    }),
                  );
                }}
                src={photo?.thumbnail_url}
                alt=""
              />
            </div>
          ))}
          {numberOfPhotos > thumbnailLimit
            ? (
              <div
                className="preview-image"
                onClick={() => {
                  dispatch(
                    updateVisibility({
                      modalType: 'imageSlidesModal',
                      isVisible: true,
                      extraProps: {
                        modalData: {
                          collection: photos,
                          position: 0,
                          showDeleteButton: false,
                          imageType,
                          jobRootNodeId,
                          hasAbilityToDownload: true,
                          timeZone,
                        },
                      },
                    }),
                  );
                }}
              >
                +
                {numberOfPhotos - thumbnailLimit}
              </div>
            )
            : ''}
        </div>
      );
    } if (isLoading) {
      return <Loader />;
    }
    return '';
  };

  return (
    <div className="preview-image-outer">
      <div className="preview-image-head">
        <p>{sectionName}</p>
      </div>
      {renderPhotos()}
    </div>
  );
};

/** ****************************** */
/* Parent component Starts here */
/** ****************************** */

interface PhotosContainerProps {
  isLoading: boolean;
  timeZone: string | undefined;
}

const PhotosContainer: React.FC<PhotosContainerProps> = ({ isLoading, timeZone }) => {
  const tenantPhotos = useAppSelector(
    (state) => state?.jobDetails?.data?.tenant_photos);

  const jobRootNodeId = useAppSelector((state) => state?.jobDetails?.data?.root_node_id);
  const beforePhotos = useAppSelector((state) => state?.jobDetails?.data?.before_photos);
  const afterPhotos = useAppSelector((state) => state?.jobDetails?.data?.after_photos);

  const photoSections = [
    ...(!isEmpty(tenantPhotos) ? [{
      sectionName: 'Tenant Media:',
      imageType: 'tenant-photos',
      photos: tenantPhotos,
    }] : []),
    ...(!isEmpty(beforePhotos) ? [{
      sectionName: 'Before Media:',
      imageType: 'before-photos',
      photos: beforePhotos,
    }] : []),
    ...(!isEmpty(afterPhotos) ? [{
      sectionName: 'After Media:',
      imageType: 'after-photos',
      photos: afterPhotos,
    }] : []),
  ];

  if (isEmpty(photoSections)) {
    return null;
  }
  const numberOfSections = photoSections?.length;

  return (
    <div className="job-content-col">
      <div className="job-content-label">Media:</div>
      <div className="job-content-data">
        <div className="preview-image-wrapper">
          {photoSections?.map((section) => (
            <Photos
              key={section.imageType}
              photos={section?.photos}
              sectionName={section?.sectionName}
              jobRootNodeId={jobRootNodeId}
              thumbnailLimit={numberOfSections > 2 ? 2 : 4}
              imageType={section?.imageType}
              isLoading={isLoading}
              timeZone={timeZone}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PhotosContainer;
